<ng-container [ngTemplateOutlet]="isDriver ? DriverTemplate : DefaultTemplate"></ng-container>
<ng-template #DriverTemplate>
  <div class="driver-login login-wrapper" fxLayout="column" fxLayoutAlign="center start" fxLayoutGap="20px" (keyup.enter)="login()">
    <img src="assets/images/rspl_logo.png" alt="" />
    <h2>Login to Your driver account</h2>

    @if (form) {
      <form [formGroup]="form">
        <div class="form-row" fxLayout="column" [ngClass]="{ invalid: showErrors && email.errors }">
          <label>Username</label>
          <input type="email" formControlName="email" placeholder="Your email" />
          @if (showErrors && email.errors && email.errors['required']) {
            <span class="error-message">Email is required</span>
          }
          @if (showErrors && email.errors && email.errors['email']) {
            <span class="error-message">Email is not valid</span>
          }
        </div>
        <div class="form-row" fxLayout="column" [ngClass]="{ invalid: showErrors && password.errors }">
          <div fxLayout="row" fxLayoutAlign="space-between">
            <label>Password</label>
            <a [href]="resetPasswordUrl" target="_blank">Forgot password?</a>
          </div>
          <input [type]="showPassword ? 'text' : 'password'" formControlName="password" placeholder="Your password" />
          <button
            mat-icon-button
            (click)="showPassword = !showPassword"
            matSuffix
            class="password-toggle"
            [ngClass]="{ invisible: !showPassword }"
          >
            <mat-icon>remove_red_eye</mat-icon>
          </button>
          @if (showErrors && password.errors && password.errors['required']) {
            <span class="error-message">Password is required</span>
          }
        </div>
        <div fxLayoutAlign="end">
          <button class="primary-btn block-btn" type="submit" (click)="login()" [ladda]="isSubmitting">Sign In</button>
        </div>
        @if (support) {
          <p>
            If you're having trouble logging in you can contact support via following number:
            <a href="tel:{{ support }}">{{ support }}</a>
          </p>
        }
      </form>
    }
  </div>
</ng-template>
<ng-template #DefaultTemplate>
  <div class="login-wrapper {{ app }}" fxLayoutAlign="center center">
    @if (isGoogleSignIn) {
      <!-- Captain: one narrow column — logo, greeting, Google first; the email/password form unfolds below on request -->
      <rspl-card class="login-card captain-card p4">
        <mat-card-content class="captain-login" fxLayout="column" fxLayoutAlign="start center">
          <mat-icon svgIcon="rspl-logo" class="captain-logo" />
          <h1 class="captain-title">ReSupply Captain</h1>
          <p class="captain-subtitle">Sign in to the Captain Portal with your &#64;resupplyme.com Google account</p>
          <div class="google-button-row" [class.busy]="isSubmitting" fxLayout="row" fxLayoutAlign="center center" fxLayoutGap="12px">
            <div #googleButton class="google-button-host" data-testid="google-sign-in-button"></div>
            @if (isSubmitting && submittingVia === 'google') {
              <mat-spinner diameter="24" data-testid="google-sign-in-spinner" />
            }
          </div>
          @if (googleSignInError) {
            <span class="error-message" data-testid="google-sign-in-error">{{ googleSignInError }}</span>
          }
          <button
            type="button"
            class="switch-link"
            (click)="showCredentials = !showCredentials"
            [disabled]="isSubmitting"
            [attr.data-testid]="showCredentials ? 'use-google-link' : 'use-credentials-link'"
          >
            {{ showCredentials ? 'Use Google sign-in instead' : 'Having trouble? Sign in with email and password' }}
          </button>
          @if (form) {
            <!-- always in the DOM so its height can animate open/closed; `inert` keeps the folded form out of
                 the tab order and away from autofill -->
            <div class="captain-credentials" [class.open]="showCredentials" [attr.inert]="showCredentials ? null : ''">
              <div class="captain-credentials-body" fxLayout="column">
                <hr />
                <h2>Sign in with email</h2>
                <ng-container [ngTemplateOutlet]="credentialsForm"></ng-container>
              </div>
            </div>
          }
        </mat-card-content>
      </rspl-card>
    } @else {
      <rspl-card [cardTitle]="'Sign In to Your ' + app + ' Portal'" class="login-card p4">
        @if (form) {
          <mat-card-content fxLayout="row" fxLayoutAlign="space-between center" fxLayoutGap="30px" fxLayoutGap.xs="0" fxPosition="static">
            <div fxFlex="50" fxFlex.xs="100" class="login-form-wrapper">
              <ng-container [ngTemplateOutlet]="credentialsForm"></ng-container>
              @if (isTPL && support) {
                <p class="support">
                  If you're having trouble logging in you can contact support via following number:
                  <a href="tel:{{ support }}">{{ support }}</a>
                </p>
              }
            </div>
            <mat-icon fxFlex svgIcon="rspl-logo" />
          </mat-card-content>
        }
      </rspl-card>
    }
  </div>
</ng-template>

<!-- the email/password form, shared by every app. It carries its own <form>: a view stamped out by
     ngTemplateOutlet takes its injector from where the template is DECLARED, so formControlName would
     not find a [formGroup] placed around the outlet. -->
<ng-template #credentialsForm>
  <form [formGroup]="form" fxLayout="column">
    <div fxLayout="column">
      <mat-form-field appearance="outline">
        <mat-label>Email</mat-label>
        <input formControlName="email" type="email" matInput />
      </mat-form-field>
    </div>
    <div fxLayout="column">
      <mat-form-field appearance="outline">
        <mat-label>Password</mat-label>
        <input [type]="showPassword ? 'text' : 'password'" formControlName="password" matInput />
        <button
          type="button"
          mat-icon-button
          (click)="showPassword = !showPassword"
          matSuffix
          class="password-toggle"
          [ngClass]="{ invisible: !showPassword }"
        >
          <mat-icon>remove_red_eye</mat-icon>
        </button>
      </mat-form-field>
      <a class="forgot-password-link" [href]="resetPasswordUrl" target="_blank">Forgot password?</a>
    </div>

    <div fxLayout="column" fxLayoutGap="20px" fxLayoutAlign="center">
      <button
        type="submit"
        mat-raised-button
        rsplDisableDoubleClick
        (oneClick)="login()"
        [ladda]="isSubmitting && submittingVia === 'email'"
        [disabled]="isSubmitting && submittingVia === 'google'"
        class="primary-btn filled-btn"
      >
        Sign In
      </button>
      @if (app === appTypes.TPL) {
        <div class="hidden-xs" fxLayout="column" fxLayoutGap="20px" fxLayoutAlign="center">
          <span class="text-center">- or create an account -</span>
          <button type="button" mat-raised-button routerLink="/onboarding" class="primary-btn">Sign Up</button>
        </div>
      }
    </div>
  </form>
</ng-template>
